import { ProductModel as ProductType } from '@/http/models/Product/Product'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useState, useRef } from 'react'

import { ProductModel as ProductModelType } from '@/types/model/Product'

import { useFetchProduct } from './useFetchProduct'
import { useFetchProducts } from './useFetchProducts'

type DialogMode = 'view'

export const useProductsTable = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null) // Ref for the file input

  const [mode, setMode] = useState<DialogMode>('view')
  const [productDialogOpen, setProductDialogOpen] = useState(false)

  const {
    product: detailedProduct,
    isLoading: isProductLoading,
    fetchProduct,
    setProduct: setDetailedProduct,
  } = useFetchProduct()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState<ProductModelType.Product | null>(null)

  const [deleteLoading, setDeleteLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [importLoading, setImportLoading] = useState(false) // State for import loading

  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchProducts({
      sort: null,
    })

  const openCreatePage = () => {
    navigate('/products/new')
  }

  const exportProducts = async () => {
    setExportLoading(true)
    try {
      const response = await ProductType.export()

      if (response.success) {
        const blob = new Blob([response.data as BlobPart], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `products-${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        toast.success('Products exported successfully')
      } else {
        toast.error('Failed to export products')
      }
    } catch (error) {
      toast.error('An error occurred during export')
    } finally {
      setExportLoading(false)
    }
  }

  // Handles the file selection and triggers the import process
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      // Basic file type validation for .xlsx
      if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && !file.name.endsWith('.xlsx')) {
        toast.error("Please upload a valid .xlsx file.");
        if (fileInputRef.current) {
          fileInputRef.current.value = ''; // Reset input
        }
        return;
      }
      importProducts(file);
    }
  };

  // Handles the actual product import API call
  const importProducts = async (file: File) => {
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      // Use the importedProduct method from ProductModel
      const response = await ProductType.import({ body: formData });

      if (response.success) {
        toast.success('Products import initiated successfully. Processing may take a moment.');
        refresh(); // Refresh the table to show updated import status
      } else {
        // Display specific error if available, otherwise a generic one
        toast.error(response.error?.data?.message || 'Failed to import products. Please check file format and content.');
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.message || 'An unexpected error occurred during import.');
    } finally {
      setImportLoading(false);
      // Clear the file input value so the same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openViewDialog = (product: ProductModelType.Product) => {
    setMode('view')
    fetchProduct(product._id)
    setProductDialogOpen(true)
  }

  const openEditPage = (product: ProductModelType.Product) => {
    navigate(`/products/${product._id}`)
  }

  const closeDialog = () => {
    setProductDialogOpen(false)
    setDetailedProduct(null)
  }

  // Delete handlers
  const handleDeleteClick = (product: ProductModelType.Product) => {
    setProductToDelete(product)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!productToDelete?._id) return

    setDeleteLoading(true)
    const response = await ProductType.delete({
      params: { id: productToDelete._id },
    })

    if (response.success) {
      toast.success('Product deleted successfully')
      setDeleteDialogOpen(false)
      setProductToDelete(null)
      refresh()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete product')
    }
    setDeleteLoading(false)
  }

  // Status toggle handler
  const toggleStatus = async (product: ProductModelType.Product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active'

    const response = await ProductType.update({
      params: { id: product._id },
      body: { status: newStatus },
    })

    if (response.success) {
      toast.success('Product status updated')
      refresh()
    } else {
      toast.error('Failed to update status')
    }
  }

  // Computed values
  const stats = {
    totalProducts: pagination.totalCount,
    activeProducts: data.filter((prod) => prod.status === 'active').length,
    inactiveProducts: data.filter((prod) => prod.status === 'inactive').length,
    lowStockProducts: 0,
  }

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    stats,

    mode,
    productDialogOpen,
    viewProduct: detailedProduct,
    isProductLoading,
    deleteDialogOpen,
    productToDelete,

    deleteLoading,
    exportLoading,
    importLoading,

    setPage,
    setPageSize,
    refresh,
    openCreatePage,
    exportProducts,
    importProducts,
    handleFileSelect,
    fileInputRef,
    openViewDialog,
    openEditPage,
    closeDialog,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    toggleStatus,
  }
}
